// A4 — THE CASE-METRICS READER. Brief 65 (brief 50 §4), the third of the sixteen.
//
// The per-row metrics both matter lists render: eleven money members handed
// back by the server's compute, plus three counts that stay client-side. It was
// `caseMetrics` in `CaseView` and `ccMetrics` in `CollectiveCaseView`, and the
// census pairs them at 85.6%:
//
//   src/views/CaseView.tsx:5620-5670@8fdbbd6c x src/views/CollectiveCaseView.tsx:2664-2718@8fdbbd6c
//
// WHICH PAIR THIS IS, because the map's A4 bullet cites a different one. The
// document lists `CollectiveCaseView.tsx:2666-2720 · CaseView.tsx:5514-5557`,
// but those two are not a census pair: `CaseView:5500-5543` pairs with
// `CCV:7353-7398` at 84.5% and that pair is TIER 2, rank 16, *the emails
// section*. Re-deriving from the census rather than reading the document (§1)
// is what separated them, and the name settles it — a *metrics reader* is the
// `pure` block that reads metrics, not the `jsx` block that renders emails.
//
// WHAT DIFFERS, AND EVERY ONE OF THEM IS A PARAMETER (§2). Five, none of them
// a choice between behaviours:
//
//   the compute cache   `finCalc`  / `ccFinCalc`
//   the task owner key  `caseId`   / `ccId`
//   the event owner key `caId`     / `ccId`   — NOT the same spelling as tasks,
//                                              which is why they are two
//                                              parameters and not one
//   the roster field    `clIds`    / `clientLinks`
//   the today string    `todayStrCa` / `todayStrCc`
//
// BOTH COPIES' COMMENTS TRAVEL, attributed. They are not duplicates of each
// other — each records a defect found on its own side (58 §1's `clIds` read
// that rendered "—" for all 254 of TEST-P22-C's claimants; 57 §1's whitelist /
// blacklist disagreement) — and dropping either would lose a finding, which is
// not what "extract, don't improve" permits.
import { countTasks } from "./taskCounters";

export function readMatterMetrics(c: any, ctx: any) {
  const {
    fin,
    tasks,
    events,
    taskOwnerKey,
    eventOwnerKey,
    rosterField,
    todayStr,
    taskStatusEntries,
  } = ctx;
  // C.2 — the money members are server-handed (POST /api/financials/compute,
  // keyed by case id on one side and cc id on the other; null until the first
  // compute lands — money cells show "—", sort comparators see null−null = 0).
  // CCV's note adds: the flat-rate basis and the union entry filter live on
  // VERBATIM in server/src/lib/caseFinancials.js (both open questions in
  // RULINGS.md, frozen — not ruled). The task/event counts stay client-side:
  // they are not money.
  const _m = fin?.[c.id]?.metrics || null;
  const bilExVAT = _m ? _m.bilExVAT : null;
  const unbExVAT = _m ? _m.unbExVAT : null;
  const bilVAT = _m ? _m.bilVAT : null;
  const unbVAT = _m ? _m.unbVAT : null;
  const paid = _m ? _m.paid : null;
  const unpaid = _m ? _m.unpaid : null;
  const bruto = _m ? _m.bruto : null;
  const netto = _m ? _m.netto : null;
  const laborCost = _m ? _m.laborCost : null;
  const grossMargin = _m ? _m.grossMargin : null;
  const marginPct = _m ? _m.marginPct : null;
  // Brief 57 §1 — the same call the dashboard's open-tasks KPI makes, with
  // the viewer omitted because this column counts the whole team's work on
  // the matter. What stood here was a blacklist of three status codes and
  // the dashboard used a whitelist of the other three; on a seventh status
  // the two screens would have disagreed with nothing to say so.
  const openTasks = countTasks(
    tasks.filter((t: any) => t[taskOwnerKey] === c.id),
    { statusEntries: taskStatusEntries },
  ).open;
  const upcomingEvts = events.filter(
    (a: any) => a[eventOwnerKey] === c.id && a.date && a.date.slice(0, 10) >= todayStr,
  ).length;
  // 58 §2 row 14 — the Clients column arrives on this list. On a CASE the field
  // is `clIds`, the Case wire alias for the client junction (shapes.js:66).
  //
  // 58 §1 — on a COLLECTIVE case it is `clientLinks`. `clIds` is a Case column
  // and `CollectiveCase` has never had one, so that read was `undefined` on
  // every row and the Clients column rendered "—" for all of them, including
  // TEST-P22-C's 254 claimants. `clientRoster` — the A1.a junction — was
  // rejected: nothing in server/src or src reads or writes it, so it never
  // reaches the browser. The two do NOT disagree: set-differenced both ways on
  // tenant 1, 0 rows.
  //
  // Which of the two names is read is the caller's, and that inversion was
  // 58 §1's defect — so it is a parameter here rather than a default.
  const numClients = (c[rosterField] || []).length;
  return {
    bilExVAT,
    unbExVAT,
    bilVAT,
    unbVAT,
    paid,
    unpaid,
    bruto,
    netto,
    openTasks,
    upcomingEvts,
    numClients,
    laborCost,
    grossMargin,
    marginPct,
  };
}
